import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, ListGroup, Button, Spinner, Alert, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import api from '../../services/api';

const FavoriteMeList = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionSuccess, setActionSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    per_page: 10,
    total_pages: 1
  });

  // Fetch users who favorited me
  useEffect(() => {
    const fetchFavoriteMeUsers = async () => {
      try {
        setLoading(true);
        const response = await api.get('/favorite-me');
        setUsers(response.data.users || []);
        
        // Set pagination data if available
        if (response.data.pagination) {
          setPagination({
            page: response.data.pagination.page || 1,
            total: response.data.pagination.total || 0,
            per_page: response.data.pagination.per_page || 10,
            total_pages: response.data.pagination.total_pages || 1
          });
        }
      } catch (err) {
        console.error('Error fetching users who favorited you:', err);
        setError('Failed to load users. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchFavoriteMeUsers();
  }, []);

  // Handle favoriting a user back
  const handleFavorite = async (username) => {
    try {
      setSubmitting(true);
      await api.post(`/users/${username}/favorite`);
      
      // Update the user in the list to show they're now favorited
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.username === username 
            ? { ...user, _links: { ...user._links, unfavorite: { href: `/users/${username}/unfavorite` } } }
            : user
        )
      );
      
      // Show success message
      setActionSuccess(`You've favorited ${username}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error favoriting user:', err);
      setError('Failed to favorite user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle unfavoriting a user
  const handleUnfavorite = async (username) => {
    try {
      setSubmitting(true);
      await api.post(`/users/${username}/unfavorite`);
      
      // Update the user in the list to show they're no longer favorited
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.username === username 
            ? { ...user, _links: { ...user._links, favorite: { href: `/users/${username}/favorite` } } }
            : user
        )
      );
      
      // Show success message
      setActionSuccess(`You've unfavorited ${username}`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setActionSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error unfavoriting user:', err);
      setError('Failed to unfavorite user. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Check if a user is favorited by the current user
  const isUserFavorited = (user) => {
    return user._links && 'unfavorite' in user._links;
  };

  // Render pagination controls
  const renderPagination = () => {
    if (pagination.total_pages <= 1) return null;

    const items = [];
    
    // Previous button
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => handlePageChange(pagination.page - 1)}
        disabled={pagination.page === 1}
      />
    );
    
    // First page
    items.push(
      <Pagination.Item 
        key={1} 
        active={pagination.page === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );
    
    // Ellipsis if needed
    if (pagination.page > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }
    
    // Pages around current page
    for (let i = Math.max(2, pagination.page - 1); i <= Math.min(pagination.total_pages - 1, pagination.page + 1); i++) {
      items.push(
        <Pagination.Item 
          key={i} 
          active={pagination.page === i}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    
    // Ellipsis if needed
    if (pagination.page < pagination.total_pages - 2) {
      items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
    }
    
    // Last page if not the first page
    if (pagination.total_pages > 1) {
      items.push(
        <Pagination.Item 
          key={pagination.total_pages} 
          active={pagination.page === pagination.total_pages}
          onClick={() => handlePageChange(pagination.total_pages)}
        >
          {pagination.total_pages}
        </Pagination.Item>
      );
    }
    
    // Next button
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => handlePageChange(pagination.page + 1)}
        disabled={pagination.page === pagination.total_pages}
      />
    );

    return (
      <Pagination className="justify-content-center mt-4">
        {items}
      </Pagination>
    );
  };

  // Handle page change
  const handlePageChange = async (page) => {
    try {
      setLoading(true);
      const response = await api.get(`/favorite-me?page=${page}`);
      setUsers(response.data.users || []);
      
      // Update pagination data
      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page || 1,
          total: response.data.pagination.total || 0,
          per_page: response.data.pagination.per_page || 10,
          total_pages: response.data.pagination.total_pages || 1
        });
      }
    } catch (err) {
      console.error('Error fetching users who favorited you:', err);
      setError('Failed to load users. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (loading && users.length === 0) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading users who favorited you...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <h2 className="fw-bold mb-4">Users Who Favorited You</h2>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {actionSuccess && (
        <Alert variant="success" className="mb-4">
          {actionSuccess}
        </Alert>
      )}

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {users.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-heart display-4 text-muted"></i>
              <p className="mt-3 text-muted">No users have favorited you yet</p>
            </div>
          ) : (
            <ListGroup variant="flush">
              {users.map(user => (
                <ListGroup.Item key={user.username} className="py-3 px-4">
                  <Row className="align-items-center">
                    <Col xs={12} md={8} className="d-flex align-items-center">
                      <div className="me-3">
                        {user.avatars?.small ? (
                          <img
                            src={user.avatars.small}
                            alt={user.username}
                            className="rounded-circle"
                            style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                          />
                        ) : (
                          <div 
                            className="rounded-circle bg-light d-flex align-items-center justify-content-center"
                            style={{ width: '50px', height: '50px' }}
                          >
                            <i className="bi bi-person text-muted" style={{ fontSize: '1.5rem' }}></i>
                          </div>
                        )}
                      </div>
                      <div>
                        <h5 className="mb-0 fw-bold">{user.username}</h5>
                        <p className="text-muted mb-0">
                          {user.age && `${user.age} • `}
                          {user.city && user.region ? `${user.city}, ${user.region}` : (user.city || user.region || '')}
                        </p>
                      </div>
                    </Col>
                    <Col xs={12} md={4} className="mt-3 mt-md-0 text-md-end">
                      <Button
                        as={Link}
                        to={`/users/${user.username}`}
                        variant="outline-primary"
                        className="me-2"
                      >
                        View Profile
                      </Button>
                      {isUserFavorited(user) ? (
                        <Button
                          variant="outline-danger"
                          onClick={() => handleUnfavorite(user.username)}
                          disabled={submitting}
                        >
                          <i className="bi bi-heart-fill me-1"></i>
                          Unfavorite
                        </Button>
                      ) : (
                        <Button
                          variant="outline-primary"
                          onClick={() => handleFavorite(user.username)}
                          disabled={submitting}
                        >
                          <i className="bi bi-heart me-1"></i>
                          Favorite Back
                        </Button>
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      {renderPagination()}
    </Container>
  );
};

export default FavoriteMeList;
